import { Instrument, RegistrationType } from '../../../services/fundingApi';

export const MAX_BATCH_SIZE = 100;
export const BATCH_CONCURRENCY = 4;

export type BatchTarget = RegistrationType | 'BOTH';
export type SelectionState = 'none' | 'some' | 'all';
export type RegisterOutcome = { ok: true } | { ok: false; message: string };
export type RegisterRequest = (instrumentId: string, type: RegistrationType) => Promise<RegisterOutcome>;
export type BatchFailure = { instrumentId: string; type: RegistrationType; message: string };
export type BatchResult = { succeeded: string[]; failures: BatchFailure[] };

const REGISTRATION_ORDER: RegistrationType[] = ['CUSTODY', 'COLLATERAL'];

const findRegistration = (instrument: Instrument, type: RegistrationType) =>
    instrument.registrations.find((r) => r.type === type);

const canRegister = (instrument: Instrument, type: RegistrationType, allowedStatus: string): boolean => {
    if (instrument.status !== 'DRAFT' && instrument.status !== allowedStatus) return false;
    const registration = findRegistration(instrument, type);
    return !registration || registration.status === 'FAILED';
};

export const canRegisterCustody = (instrument: Instrument): boolean =>
    canRegister(instrument, 'CUSTODY', 'COLLATERAL_REGISTERED');

export const canRegisterCollateral = (instrument: Instrument): boolean =>
    canRegister(instrument, 'COLLATERAL', 'CUSTODY_REGISTERED');

const canRegisterType = (instrument: Instrument, type: RegistrationType): boolean =>
    type === 'CUSTODY' ? canRegisterCustody(instrument) : canRegisterCollateral(instrument);

export const typesFor = (instrument: Instrument, target: BatchTarget): RegistrationType[] =>
    REGISTRATION_ORDER.filter((type) => (target === 'BOTH' || target === type) && canRegisterType(instrument, type));

export const eligibleInstruments = (instruments: Instrument[], target: BatchTarget): Instrument[] =>
    instruments.filter((instrument) => typesFor(instrument, target).length > 0);

export const selectionState = (eligible: Instrument[], selectedIds: string[]): SelectionState => {
    const marked = eligible.filter((instrument) => selectedIds.includes(instrument.id)).length;
    if (marked === 0) return 'none';
    return marked === eligible.length ? 'all' : 'some';
};

export const toggleAllIds = (eligible: Instrument[], selectedIds: string[]): string[] =>
    selectionState(eligible, selectedIds) === 'all' ? [] : eligible.map((instrument) => instrument.id);

export const batchProblem = (count: number): string | null => {
    if (count === 0) return 'Selecione ao menos uma emissão.';
    if (count > MAX_BATCH_SIZE) return `Selecione no máximo ${MAX_BATCH_SIZE} emissões por envio.`;
    return null;
};

const outcomeOf = async (register: RegisterRequest, instrumentId: string, type: RegistrationType): Promise<RegisterOutcome> => {
    try {
        return await register(instrumentId, type);
    } catch (error) {
        return { ok: false, message: error instanceof Error ? error.message : 'erro inesperado' };
    }
};

const registerInOrder = async (
    instrument: Instrument,
    types: RegistrationType[],
    register: RegisterRequest,
): Promise<BatchFailure | null> => {
    for (const type of types) {
        const outcome = await outcomeOf(register, instrument.id, type);
        if (!outcome.ok) return { instrumentId: instrument.id, type, message: outcome.message };
    }
    return null;
};

export const runBatch = async (
    instruments: Instrument[],
    target: BatchTarget,
    register: RegisterRequest,
    concurrency: number = BATCH_CONCURRENCY,
): Promise<BatchResult> => {
    const result: BatchResult = { succeeded: [], failures: [] };
    const queue = instruments
        .map((instrument) => ({ instrument, types: typesFor(instrument, target) }))
        .filter((entry) => entry.types.length > 0);
    let next = 0;

    const worker = async () => {
        while (next < queue.length) {
            const { instrument, types } = queue[next++];
            const failure = await registerInOrder(instrument, types, register);
            if (failure) result.failures.push(failure);
            else result.succeeded.push(instrument.id);
        }
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
    return result;
};

export const summaryText = (result: BatchResult): string => {
    const sent = result.succeeded.length;
    return `${sent} ${sent === 1 ? 'enviada' : 'enviadas'}, ${result.failures.length} com erro`;
};

export const retryableIds = (result: BatchResult): string[] => result.failures.map((failure) => failure.instrumentId);
